import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, MapPin, Star, MessagesSquare, ArrowRight } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useLang } from "../context/LangContext";

export default function Suppliers() {
  const { t } = useLang();
  const [suppliers, setSuppliers] = useState(null);

  useEffect(() => {
    api("/suppliers").then(setSuppliers).catch(() => setSuppliers([]));
  }, []);

  return (
    <div data-testid="suppliers-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <p className="text-xs uppercase font-bold tracking-[0.2em] text-teal-700">{t("verified_trusted")}</p>
      <h1 className="font-heading mt-1 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
        {t("sup_t")}
      </h1>
      <p className="mt-1 text-sm text-slate-600">{t("sup_sub")}</p>

      {suppliers === null ? (
        <p data-testid="suppliers-loading" className="py-16 text-center text-sm text-slate-500">...</p>
      ) : suppliers.length === 0 ? (
        <div data-testid="suppliers-empty" className="mt-8 rounded-2xl border border-dashed border-slate-300 py-16 text-center">
          <p className="font-heading font-semibold text-slate-800">{t("empty_t")}</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {suppliers.map((s) => (
            <div
              key={s.id}
              data-testid={`supplier-card-${s.id}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="h-32 overflow-hidden bg-teal-50">
                {s.image && <img src={mediaUrl(s.image)} alt={s.name} loading="lazy" className="h-full w-full object-cover" />}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-1.5">
                  <h3 className="font-heading font-bold text-slate-900 truncate">{s.name}</h3>
                  {s.verified && <BadgeCheck data-testid={`supplier-verified-${s.id}`} className="h-4 w-4 shrink-0 text-teal-600" />}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.country}</span>
                  <span className="flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {s.rating}</span>
                  <span className="flex items-center gap-1"><MessagesSquare className="h-3 w-3" /> {s.response_rate}% {t("response")}</span>
                </div>
                <Link
                  data-testid={`supplier-products-${s.id}`}
                  to={`/store/${s.slug || s.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-700 px-4 py-2 text-xs font-bold text-white hover:bg-teal-800 transition active:scale-95"
                >
                  {t("view_products")} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
